import { useState, useEffect } from "react";
import { Settings, Bell, HelpCircle, ChevronDown, Globe, Check, Menu, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "@/context/SidebarContext";
import StepNavigation from "@/components/StepNavigation";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchEmployeeName } from "@/services/employeeService";
import { parseStateField } from "@/utils/locationState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { isExpanded, toggleMobile } = useSidebar();
  const [currentStep, setCurrentStep] = useState(1);
  const [showStepperInHeader, setShowStepperInHeader] = useState(false);
  const [employeeFirstName, setEmployeeFirstName] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const employeeIdFromState = parseStateField(location.state, "employeeId") ?? null;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const stepperPrefixes: Array<{ prefix: string; step: number }> = [
    { prefix: "/evaluation", step: 1 },
    { prefix: "/assessment-results", step: 2 },
    { prefix: "/prepare-meeting", step: 3 },
    { prefix: "/meet-employee", step: 4 },
  ];
  // Match both legacy ("/evaluation") and dynamic ("/evaluation/:evaluationId") URLs.
  const matchedStepper = stepperPrefixes.find(
    ({ prefix }) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  );
  const isStepperPage = !!matchedStepper;

  useEffect(() => {
    setShowStepperInHeader(isStepperPage);
    if (matchedStepper) setCurrentStep(matchedStepper.step);
  }, [location, isStepperPage, matchedStepper]);

  useEffect(() => {
    const empId = employeeIdFromState;
    if (!empId) { setEmployeeFirstName(null); return; }
    fetchEmployeeName(empId).then((data) => {
      if (data) setEmployeeFirstName(`${data.first_name} ${data.last_name}`);
    });
  }, [employeeIdFromState]);

  const isEmployeeProfile = location.pathname === "/employee-profile";

  const headerTitle = isStepperPage && employeeFirstName
    ? `${t('header.evaluationOf', { name: employeeFirstName })}`
    : null;

  return (
    <header
      data-app-header
      className={`fixed top-0 left-0 right-0 w-full px-3 md:px-6 flex flex-col z-50 bg-white/50 backdrop-blur-[30px] border-b border-white/30 transition-all duration-500 ${
        showStepperInHeader ? "h-auto" : "h-14 md:h-16"
      }`}
    >
      {/* Main Header Content */}
      <div className="flex items-center justify-between w-full h-14 md:h-16">
        {/* Mobile Menu Button — uses SidebarContext instead of CustomEvent */}
        <button
          className="md:hidden p-2 hover:bg-muted rounded-lg transition-all mr-2"
          onClick={toggleMobile}
        >
          <Menu className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2F7598ff8c43334f4ba7f31278d001a715%2F2d1f6eb2615f4760807df50fbba39686?format=webp&width=800"
            alt={t("altText.amelioLogo")}
            className="w-auto h-5 md:h-6"
          />
        </div>

        {/* Title */}
        {headerTitle && (
          <div
            className={`hidden md:flex flex-col items-start gap-1 flex-1 transition-all duration-300 ${
              isExpanded ? "ml-[263px]" : "ml-[88px]"
            }`}
          >
            <h1 className="font-medium text-navy text-sm truncate max-w-[300px]">
              {headerTitle}
            </h1>
            <div className="bg-turquoise rounded-full w-12 h-0.5" />
          </div>
        )}
        {!headerTitle && <div className={`hidden md:block flex-1 transition-all duration-300 ${isExpanded ? "ml-[263px]" : "ml-[88px]"}`} />}

        {/* Mobile spacer */}
        <div className="flex-1 md:hidden" />

        {/* Right Side */}
        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-1 md:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 hover:bg-muted rounded-lg transition-all opacity-60 outline-none">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage("fr")} className="justify-between">
                  Français
                  {i18n.language === "fr" && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage("en")} className="justify-between">
                  English
                  {i18n.language === "en" && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button className="hidden sm:block p-1.5 hover:bg-muted rounded-lg transition-all opacity-60">
              <Settings className="w-5 h-5 text-muted-foreground" />
            </button>
            <button className="p-1.5 hover:bg-muted rounded-lg transition-all relative opacity-60">
              <Bell className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground" />
            </button>
            <button className="hidden sm:block p-1.5 hover:bg-muted rounded-lg transition-all opacity-60">
              <HelpCircle className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* User Profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 md:gap-2 ml-1 md:ml-3 outline-none cursor-pointer">
                  <div className="rounded-full bg-accent-purple text-white flex items-center justify-center w-7 h-7 md:w-8 md:h-8 text-xs font-semibold">
                    ML
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-foreground">Maxime Lacroix</span>
                  <ChevronDown className="hidden sm:block text-navy flex-shrink-0 w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={async () => {
                    const { supabase } = await import("@/integrations/supabase/client");
                    await supabase.auth.signOut();
                    navigate("/login");
                  }}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('sidebar.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Step Navigation (Conditional) */}
      {showStepperInHeader && (
        <div className="w-full py-2 md:py-3 px-0 border-t border-white/20">
          <div className="px-2 md:px-6">
            <div className="max-w-[1330px] mx-auto">
              <StepNavigation currentStep={currentStep} compact={true} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
